import { Button, FormGroup, MenuItem } from "@blueprintjs/core";
import { Select } from "@blueprintjs/select";
import { ItemSelect, ItemSelectButton } from "@package/ui-apollo-components";
import { useAuthState } from "@package/ui-authentication";
import { WallButton } from "@package/ui-components";
import { EventPayloadType } from "@package/ui-gql/index-analytics";
import {
  GetOrganizationListNameIdDocument,
  GetProfileListNameIdDocument,
  type GetOrganizationListNameIdQuery,
  type GetOrganizationListNameIdQueryVariables,
  type GetProfileListNameIdQuery,
  type GetProfileListNameIdQueryVariables,
} from "@package/ui-gql/index-data-access";
import { useDispatchWall, useSelectorWall } from "@package/ui-redux";
import {
  sliceWallUpdateVideoSource,
  type SliceWallScreenState,
} from "@package/ui-wall-engine";
import { useEffect, useState } from "react";
import { useWallAnalytics } from "../../context/context-analytics/context-analytics";
import { useWallConfig } from "../../context/context-wall-config/context-wall-config";
import { useWallProvider } from "../../context/context-wall/context-wall";
import { ScreenMenuControlGroup } from "./ScreenMenuControlGroup";

type WallMediaDeviceInfo = {
  deviceId?: string;
  label: string;
};

type ScreenMenuProps = {
  screen: SliceWallScreenState;
};

export function ScreenMenu({ screen }: ScreenMenuProps) {
  const { organization, profile, update } = useWallConfig();
  const { token, tokenDecoded } = useAuthState();
  const { trigger } = useWallAnalytics();
  const device = useSelectorWall((state) => state.wall.device);
  const dispatch = useDispatchWall();
  const provider = useWallProvider();
  const videoSource = useSelectorWall((state) => state.wall.videoSource);

  const [organizationLocal, setOrganizationLocal] = useState<
    string | undefined
  >(organization);
  const [videoSources, setVideoSources] = useState<WallMediaDeviceInfo[]>([]);
  const videoSourceActive = videoSources?.find(
    (v) => v.deviceId === videoSource,
  );

  useEffect(
    function effectOnOrganizationChange() {
      setOrganizationLocal(organization);
    },
    [organization],
  );

  useEffect(() => {
    const getVideoSources = async () => {
      try {
        const sources =
          (await navigator?.mediaDevices?.enumerateDevices()) ?? [];
        const sourcesVideo = sources.filter((v) => v.kind === "videoinput");
        const sourcesWall: WallMediaDeviceInfo[] = sourcesVideo.map(
          (source) => ({
            deviceId: source.deviceId,
            label: source.label,
          }),
        );

        sourcesWall.splice(0, 0, {
          deviceId: undefined,
          label: "PanoScape",
        });

        setVideoSources(sourcesWall);
      } catch (error: unknown) {
        console.error("Error fetching video sources:", error);
      }
    };

    getVideoSources();
  }, [provider]);

  if (!screen.menu) {
    return null;
  }

  return (
    <div className="absolute h-full w-full top-0 left-0 z-20 bg-black">
      <div className="flex flex-col items-center justify-center h-full w-full">
        <div className="text-5xl text-slate-100 mb-8">Wall Canvas</div>
        <ScreenMenuControlGroup>
          <FormGroup
            className="text-3xl text-slate-400 w-full mb-8"
            label="Organization"
          >
            <ItemSelect<
              GetOrganizationListNameIdQuery,
              GetOrganizationListNameIdQueryVariables
            >
              document={GetOrganizationListNameIdDocument}
              activeItemId={organizationLocal}
              fetchPolicy="network-only"
              filterable={false}
              onItemSelected={(id) => {
                setOrganizationLocal(id ?? undefined);
              }}
              sorted
              variables={{
                orgIds:
                  tokenDecoded?.["https://v1.panoscape.app/organizations"] ??
                  [],
              }}
            >
              <ItemSelectButton className="text-3xl text-slate-100 outline-none" />
            </ItemSelect>
          </FormGroup>
          <FormGroup
            className="text-3xl text-slate-400 w-full mb-8"
            label="Canvas"
          >
            <ItemSelect<
              GetProfileListNameIdQuery,
              GetProfileListNameIdQueryVariables
            >
              key={organizationLocal}
              document={GetProfileListNameIdDocument}
              activeItemId={profile}
              context={{
                headers: {
                  Authorization: `Bearer ${token}`,
                  "x-org-id": organizationLocal,
                },
              }}
              disabled={!organizationLocal}
              fetchPolicy="network-only"
              filterable={false}
              onItemSelected={(id) => {
                provider?.onScreenMenuClosed(screen.id);
                update((draft) => {
                  draft.organization = organizationLocal ?? undefined;
                  draft.profile = id ?? undefined;
                });
                trigger(EventPayloadType.ProfileLoaded, {
                  profile: id ?? undefined,
                });
              }}
              sorted
            >
              <ItemSelectButton className="text-3xl text-slate-100 outline-none" />
            </ItemSelect>
          </FormGroup>
        </ScreenMenuControlGroup>
        <div className="text-5xl text-slate-100 mb-8">Video Input</div>
        <ScreenMenuControlGroup>
          <Select<WallMediaDeviceInfo>
            activeItem={videoSourceActive}
            className="text-3xl text-slate-100"
            disabled={!device?.screens?.videoSource?.enabled}
            filterable={false}
            items={videoSources}
            itemRenderer={(source, { handleClick, handleFocus, modifiers }) => (
              <MenuItem
                active={modifiers.active}
                disabled={modifiers.disabled}
                key={source.deviceId}
                onClick={handleClick}
                onMouseDown={handleClick}
                onFocus={handleFocus}
                roleStructure="listoption"
                text={source.label}
              />
            )}
            popoverProps={{ minimal: true }}
            onItemSelect={(type) => {
              provider?.onScreenMenuClosed(screen.id);
              dispatch(sliceWallUpdateVideoSource(type.deviceId ?? undefined));
              trigger(EventPayloadType.VideoSource, {
                id: type.deviceId,
                name: type.label,
              });
            }}
          >
            <Button
              alignText="left"
              className="text-3xl text-slate-100 outline-none"
              disabled={!device?.screens?.videoSource?.enabled}
              fill
              minimal
              outlined
              rightIcon="caret-down"
              text={videoSourceActive?.label ?? "Select a Video Source"}
            />
          </Select>
        </ScreenMenuControlGroup>
        <div className="absolute bottom-4 right-0">
          <WallButton
            align="right"
            icon="cross"
            intent="danger"
            onHandleMouse={() => {
              setOrganizationLocal(organization);
              provider?.onScreenMenuClosed(screen.id);
            }}
          />
        </div>
      </div>
    </div>
  );
}
